// Topic-drift detection: cosine similarity between the original query and the
// snippets of each retrieved citation. Used to flag off-topic corpora and
// abort runs whose retrieved evidence does not actually answer the question.

import { embedTexts } from "./api";

export interface DriftReport {
  avgSimilarity: number;
  perCitation: Array<{ index: number; similarity: number; offTopic: boolean }>;
  offTopicCount: number;
  totalCount: number;
  status: "ok" | "drift" | "insufficient";
  badge?: "TOPIC_DRIFT_DETECTED";
}

const ON_TOPIC_THRESHOLD = 0.55;
const ABORT_RATIO = 0.7; // > 70% off-topic → abort

function cosine(a: number[], b: number[]): number {
  let dot = 0, na = 0, nb = 0;
  const n = Math.min(a.length, b.length);
  for (let i = 0; i < n; i++) {
    dot += a[i] * b[i];
    na += a[i] * a[i];
    nb += b[i] * b[i];
  }
  if (na === 0 || nb === 0) return 0;
  return dot / (Math.sqrt(na) * Math.sqrt(nb));
}

/** Cheap deterministic embedding fallback when no embed provider is configured. */
function localEmbed(text: string, dim = 256): number[] {
  const v = new Array<number>(dim).fill(0);
  const tokens = text.toLowerCase().match(/[a-z0-9]+/g) ?? [];
  for (const t of tokens) {
    let h = 5381;
    for (let i = 0; i < t.length; i++) h = ((h << 5) + h + t.charCodeAt(i)) | 0;
    v[Math.abs(h) % dim] += 1;
  }
  // L2 normalize
  const norm = Math.sqrt(v.reduce((s, x) => s + x * x, 0)) || 1;
  return v.map((x) => x / norm);
}

export async function detectTopicDrift(
  query: string,
  snippets: string[],
  signal?: AbortSignal,
): Promise<DriftReport> {
  if (!snippets.length) {
    return { avgSimilarity: 0, perCitation: [], offTopicCount: 0, totalCount: 0, status: "insufficient" };
  }
  if (signal?.aborted) {
    return { avgSimilarity: 0, perCitation: [], offTopicCount: 0, totalCount: snippets.length, status: "ok" };
  }

  let queryVec: number[];
  let docVecs: number[][];
  try {
    const all = await embedTexts({ texts: [query, ...snippets] });
    if (!all.ok) throw new Error("embed_unavailable");
    queryVec = all.data.embeddings[0];
    docVecs = all.data.embeddings.slice(1);
  } catch {
    // Graceful fallback: deterministic local hashing embeddings
    queryVec = localEmbed(query);
    docVecs = snippets.map((s) => localEmbed(s));
  }

  const perCitation = docVecs.map((v, i) => {
    const sim = cosine(queryVec, v);
    return { index: i, similarity: sim, offTopic: sim < ON_TOPIC_THRESHOLD };
  });
  const offTopicCount = perCitation.filter((c) => c.offTopic).length;
  const avgSimilarity = perCitation.reduce((s, c) => s + c.similarity, 0) / perCitation.length;

  let status: DriftReport["status"] = "ok";
  let badge: DriftReport["badge"];
  const ratio = offTopicCount / perCitation.length;
  if (ratio > ABORT_RATIO) status = "insufficient";
  else if (avgSimilarity < ON_TOPIC_THRESHOLD) {
    status = "drift";
    badge = "TOPIC_DRIFT_DETECTED";
  }

  return { avgSimilarity, perCitation, offTopicCount, totalCount: perCitation.length, status, badge };
}
